import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { DestructiveDialog, Icon, Popover, Row, ThemeToggle } from '@efira/ui';

import { signOut } from '../../auth/client';
import { useCurrentUser } from '../../auth/session';
import { PlanName, ProStatus } from '../ProStatus';

/**
 * Меню аккаунта в левой трети полосы: аватар с инициалами и семь строк.
 *
 * Состав повторяет эталон (docs/reference/LIVECADE_UX.md, раздел 3): шапка с
 * именем, тарифом и почтой; аналитика, история, настройки; апгрейд с подписью
 * акции; партнёрская программа; выход.
 *
 * Выход - через подтверждение: это единственное действие меню, которое нельзя
 * отменить нажатием «назад».
 */
export function AccountMenu() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [signOutOpen, setSignOutOpen] = useState(false);

  const initials = (user?.name ?? user?.email ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return (
    <>
      <Popover
        label="Меню аккаунта"
        triggerClassName="gap-1 px-1"
        className="min-w-64 p-0"
        trigger={
          <>
            <span
              data-topbar="avatar"
              className="bg-primary text-primary-foreground grid size-7 place-items-center rounded-full text-xs font-semibold"
            >
              {initials}
            </span>
            <Icon name="chevronDown" size="sm" />
          </>
        }
      >
        <div data-topbar="account-head" className="border-foreground/10 border-b px-3 py-3">
          <p className="flex items-center gap-2 text-sm font-medium">
            <span className="truncate">{user?.name ?? 'Аккаунт'}</span>
            <PlanName />
          </p>
          <p className="text-foreground/80 truncate text-sm">{user?.email ?? ''}</p>
        </div>

        {/*
          ОДИН ШАБЛОН СТРОКИ НА ВСЁ МЕНЮ - роль «строка списка» (`Row`).

          До единицы 17 пункты-ссылки несли только `gap-2`: `Link`
          react-router рисует обычный `a`, ряда из него не получается, и
          значок вставал НАД подписью у четырёх пунктов из шести. У «Темы»
          и «Выйти» flex был прописан руками - отсюда два разных вида в
          одном меню (аудит единицы 16, находка 08).
        */}
        <ul className="p-2">
          {(
            [
              ['/app/analytics/overview', 'chart', 'Аналитика'],
              ['/app/history', 'history', 'История эфиров'],
              ['/app/settings/profile', 'settings', 'Настройки'],
            ] as const
          ).map(([to, icon, title]) => (
            <li key={to}>
              <Row asChild size="sm" media={<Icon name={icon} size="sm" />} title={title}>
                <Link to={to} data-popover-close />
              </Row>
            </li>
          ))}
        </ul>

        <div className="border-foreground/10 border-y p-2">
          <Row
            asChild
            size="sm"
            media={<Icon name="crown" size="sm" tone="warning" />}
            title={
              <span className="flex items-center gap-2">
                Тариф Pro
                {/* Слово - из одного источника со всеми остальными
                    местами (`ProStatus`): до единицы 21 здесь было «в
                    разработке», а окно тарифа в это время продавало. */}
                <ProStatus />
              </span>
            }
            /* Без цены, скидки и таймера: оплаты в продукте нет, и обещать
               срок несуществующей акции нельзя. */
            description="Без дневного лимита эфира и с платными играми"
          >
            {/* `data-left="promo-card"` - пункт реестра «карточка акции»
                переехал сюда с первого экрана консоли решением владельца
                2026-09-26 (3). Пометка едет вместе с ним: пункт реестра
                привязан к НАЗНАЧЕНИЮ, а не к месту, и его переезд записан
                в `docs/DIVERGENCE.md`. */}
            <Link
              to="/app/plan?from=quota"
              data-topbar="account-upgrade"
              data-left="promo-card"
              data-popover-close
            />
          </Row>
        </div>

        <ul className="p-2">
          <li>
            <Row
              asChild
              size="sm"
              media={<Icon name="handshake" size="sm" />}
              title="Партнёрская программа"
            >
              <Link to="/app/affiliate" data-popover-close />
            </Row>
          </li>
          <li>
            <Row
              size="sm"
              media={<Icon name="sun" size="sm" />}
              title="Тема"
              actions={<ThemeToggle />}
            />
          </li>
          <li>
            <Row
              asChild
              size="sm"
              /* Цветом помечена ИКОНКА: цветной текст error на светлой теме
                 даёт 3,5:1 и не проходит AA, а смысл несёт подтверждение. */
              media={<Icon name="signOut" size="sm" tone="danger" />}
              title="Выйти"
            >
              {/* eslint-disable-next-line no-restricted-syntax -- `asChild`
                  строки требует голого элемента; содержимое вкладывает Row. */}
              <button
                type="button"
                data-topbar="account-signout"
                data-destructive="sign-out"
                data-danger=""
                data-popover-close
                onClick={() => {
                  setSignOutOpen(true);
                }}
              />
            </Row>
          </li>
        </ul>
      </Popover>

      <DestructiveDialog
        id="sign-out"
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title="Выйти из аккаунта?"
        confirm="Выйти"
        onConfirm={() => {
          void signOut().then(() => navigate('/login'));
        }}
      >
        Сессия закроется на этом устройстве. Настройки игр, привязки и ссылка оверлея сохранятся.
      </DestructiveDialog>
    </>
  );
}
